import { createFileRoute } from "@tanstack/react-router";
import { physicianSchema, safeJsonLd } from "@/lib/structured-data-core";
import { lazy, Suspense, useState } from "react";
import { Play, X, User, GraduationCap, Award, LayoutGrid } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { SocialPosts } from "@/components/site/SocialPosts";
import hero from "@/assets/restore/dr-harris-scott.webp.asset.json";
import signature from "@/assets/restore/harris-signature.jpg.asset.json";
import g1 from "@/assets/restore/harris-g1.png.asset.json";
import g2 from "@/assets/restore/harris-g2.png.asset.json";
import g3 from "@/assets/restore/harris-g3.png.asset.json";
import g4 from "@/assets/restore/harris-g4.png.asset.json";
import { primaryVideoObject } from "@/lib/video-seo";
import { absoluteUrl } from "@/lib/absolute-url";

const Results = lazy(() => import("@/components/site/Results").then(m => ({ default: m.Results })));
const Reviews = lazy(() => import("@/components/site/Reviews").then(m => ({ default: m.Reviews })));
const QuoteForm = lazy(() => import("@/components/site/QuoteForm").then(m => ({ default: m.QuoteForm })));


export const Route = createFileRoute("/doctors/dr-james-harris")({
  head: () => ({
    links: [{ rel: "canonical", href: "https://restorehair.com/doctors/dr-james-harris/" }],
    meta: [
      { title: "Dr. James Harris — Medical Director | RESTORE® Hair" },
      {
        name: "description",
        content:
          "Dr. James Harris is the Medical Director of RESTORE® Hair, inventor of the Harris S.A.F.E. System, and a pioneer of FUE hair restoration.",
      },
      { property: "og:title", content: "Dr. James Harris — Medical Director" },
      {
        property: "og:description",
        content:
          "Meet Dr. James Harris, world-renowned pioneer of FUE hair restoration and RESTORE® Hair Medical Director.",
      },
      { property: "og:image", content: absoluteUrl(hero.url) },
      { name: "twitter:image", content: absoluteUrl(hero.url) },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: safeJsonLd(primaryVideoObject("/doctors/dr-james-harris/")),
      },
      {
        type: "application/ld+json",
        children: safeJsonLd(
          physicianSchema({
            name: "Dr. James Harris",
            jobTitle: "Medical Director",
            description: "Dr. James Harris is the Medical Director of RESTORE® Hair, inventor of the Harris S.A.F.E. System, and a pioneer of FUE hair restoration.",
            image: hero.url,
            pageUrl: "https://restorehair.com/doctors/dr-james-harris/",
          }),
        ),
      },
    ],
  }),
  component: DrHarrisPage,
});

const GALLERY = [g1.url, g2.url, g3.url, g4.url];
const VIDEO_URL =
  "https://d17ehmrb6zqtjv.cloudfront.net/Videos/Videos%20with%20Captions/restore_docbio_harris2022withpatients_v2(Original)_1CompressedForWebsite.mp4";
const HERO_FALLBACK_URL =
  "https://assets.restorehair.com/wp/2024/10/dr_harris_scott-768x1282-1.webp";

function HeroImageBlock({ setVideoOpen }: { setVideoOpen: (open: boolean) => void }) {
  return (
    <div className="flex flex-col">
      <div className="mx-auto grid w-full max-w-2xl grid-cols-2 gap-4 pb-2 text-center lg:mr-0">
        <div>
          <span className="inline-block bg-primary/25 px-3 py-1 text-xs md:text-sm font-medium text-foreground">
            Award-Winning Hair Doctor
          </span>
          <p className="mt-2 text-base md:text-lg font-bold text-foreground">Dr. James Harris</p>
          <p className="text-xs md:text-sm italic text-muted-foreground">RESTORE Medical Director</p>
        </div>
        <div className="self-end">
          <p className="text-base md:text-lg font-bold text-foreground">Scott N.</p>
          <p className="text-xs md:text-sm italic text-muted-foreground">RESTORE Client</p>
        </div>
      </div>
      <div className="relative flex justify-center lg:justify-end">
        <img
          src={hero.url}
          alt="Dr. James Harris with RESTORE client Scott N."
          className="block h-auto w-full max-w-md object-contain"
          loading="eager"
          data-publish-image-check
          onError={(event) => {
            if (event.currentTarget.src !== HERO_FALLBACK_URL) {
              event.currentTarget.src = HERO_FALLBACK_URL;
            }
          }}
        />
        <button
          type="button"
          onClick={() => setVideoOpen(true)}
          aria-label="Play Dr. James Harris' video"
          title="Play Video"
          className="absolute left-1/2 top-1/2 flex h-20 w-20 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-primary text-primary-foreground transition hover:opacity-90 md:h-24 md:w-24"
        >
          <Play className="ml-1 h-9 w-9 fill-current md:h-11 md:w-11" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

function DrHarrisPage() {
  const [tab, setTab] = useState<"overview" | "bio" | "education" | "certificates">("overview");
  const [videoOpen, setVideoOpen] = useState(false);


  return (
    <div className="min-h-screen bg-background font-sans">
      <Header />
      <main>
        {/* Hero */}
        <section className="overflow-hidden bg-background">
          <div className="mx-auto max-w-7xl px-4 pt-10">
            {/* Desktop: text left, image right */}
            <div className="hidden lg:grid lg:grid-cols-2 lg:gap-4">
              <div className="pb-16">
                <img
                  src={signature.url}
                  alt="Dr. James Harris signature"
                  className="mb-4 h-12 w-auto opacity-70"
                />
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground">
                  Dr. James Harris
                </h1>
                <p className="mt-3 text-lg md:text-xl font-semibold text-primary">
                  Medical Director &amp; Hair Restoration Physician
                </p>
                <div className="mt-4 space-y-3 text-base md:text-lg leading-relaxed text-muted-foreground">
                  <p className="font-bold text-foreground">Leading Innovations in Hair Restoration.</p>
                  <p>
                    Our Medical Director, Dr. James Harris, is a recognized pioneer—an innovator
                    behind techniques and tools so advanced, even competitors rely on what he
                    created. But tools alone aren’t enough.
                  </p>
                  <p>
                    RESTORE’s strength lies in medical expertise, aesthetic precision, and results
                    that speak for themselves. We’re not just in the game—we wrote the playbook.
                    Professional athletes, public figures, and thousands of men who demand the
                    best–all trust one name: RESTORE.
                  </p>
                  <p>
                    Over his 25-year career, Board Certified Hair Restoration Surgeon Dr. Harris
                    has performed thousands of successful procedures and trained hundreds of
                    doctors worldwide on his hair transplantation methods. As an acclaimed author
                    and international public speaker, he has been featured on TV and talk shows as
                    an expert in the evolving landscape of hair restoration.
                  </p>
                </div>
              </div>
              <div className="flex flex-col justify-end self-stretch">
                <HeroImageBlock setVideoOpen={setVideoOpen} />
              </div>
            </div>

            {/* Mobile: image appears after "Leading Innovations..." */}
            <div className="lg:hidden">
              <div className="pb-10">
                <img
                  src={signature.url}
                  alt="Dr. James Harris signature"
                  className="mb-4 h-12 w-auto opacity-70"
                />
                <p className="text-4xl md:text-5xl font-bold tracking-tight text-foreground">
                  Dr. James Harris
                </p>
                <p className="mt-3 text-lg md:text-xl font-semibold text-primary">
                  Medical Director &amp; Hair Restoration Physician
                </p>
                <div className="mt-4 space-y-3 text-base md:text-lg leading-relaxed text-muted-foreground">
                  <p className="font-bold text-foreground">Leading Innovations in Hair Restoration.</p>
                  <HeroImageBlock setVideoOpen={setVideoOpen} />
                  <p>
                    Our Medical Director, Dr. James Harris, is a recognized pioneer—an innovator
                    behind techniques and tools so advanced, even competitors rely on what he
                    created. But tools alone aren’t enough.
                  </p>
                  <p>
                    RESTORE’s strength lies in medical expertise, aesthetic precision, and results
                    that speak for themselves. We’re not just in the game—we wrote the playbook.
                    Professional athletes, public figures, and thousands of men who demand the
                    best–all trust one name: RESTORE.
                  </p>
                  <p>
                    Over his 25-year career, Board Certified Hair Restoration Surgeon Dr. Harris
                    has performed thousands of successful procedures and trained hundreds of
                    doctors worldwide on his hair transplantation methods. As an acclaimed author
                    and international public speaker, he has been featured on TV and talk shows as
                    an expert in the evolving landscape of hair restoration.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {videoOpen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/80 p-4"
            role="dialog"
            aria-modal="true"
            onClick={() => setVideoOpen(false)}
          >
            <div className="relative w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                onClick={() => setVideoOpen(false)}
                aria-label="Close video"
                className="absolute -top-10 right-0 text-background"
              >
                <X size={28} />
              </button>
              <video src={VIDEO_URL} controls autoPlay className="w-full" />
            </div>
          </div>
        )}

        {/* Tabs */}
        <section className="bg-muted/40 py-12 md:py-16">
          <div className="mx-auto w-full max-w-6xl px-4">
            <div className="grid max-w-5xl grid-cols-2 gap-1 md:grid-cols-4">
              {([
                ["overview", "Overview", LayoutGrid],
                ["bio", "Full Bio", User],
                ["education", "Education", GraduationCap],
                ["certificates", "Certificates", Award],
              ] as const).map(([key, label, Icon]) => (

                <button
                  key={key}
                  type="button"
                  onClick={() => setTab(key)}
                  className={`inline-flex items-center justify-center gap-2 px-2 py-4 text-sm font-semibold whitespace-nowrap transition md:px-6 md:py-5 md:text-lg ${
                    tab === key
                      ? "bg-primary text-primary-foreground"
                      : "bg-card text-foreground shadow-sm hover:bg-card/80"
                  }`}
                >
                  <Icon size={20} className="hidden md:inline-block" />
                  {label}
                </button>
              ))}
            </div>

            <div className="bg-card px-4 py-10 md:px-10 md:py-14 shadow-sm">
              {tab === "overview" && (
                <div className="mx-auto max-w-6xl">
                  <div>
                    <h2 className="text-2xl md:text-3xl font-bold text-foreground">
                      Dr. James Harris Pioneering the Future of FUE
                    </h2>
                    <div className="mt-4 space-y-4 text-base leading-relaxed text-muted-foreground">
                      <p>
                        Recognized worldwide as the foremost authority in hair restoration, Dr.
                        James Harris, leads our team of elite hair surgeons as the medical
                        director at RESTORE<sup>®</sup>. Celebrated for revolutionizing the hair
                        restoration industry, Harris invented the{" "}
                        <strong>Harris S.A.F.E. System</strong>, a patented medical device used
                        by doctors around the world to perform the F.U.E. procedure.
                      </p>
                      <p>
                        Dr. Harris has devoted his entire career to the advancement of hair
                        restoration and specifically F.U.E (Follicular Unit Excision). Appointed
                        by his peers as the distinguished International Chairman of the F.U.E.
                        Research Committee, he has received numerous awards including the ISHRS'
                        Golden Follicle Award, the highest honor in the field of hair
                        restoration, bestowed upon only one doctor worldwide each year for
                        significant clinical contributions to the field of hair restoration.
                      </p>
                      <p>
                        As Chairman of the F.U.E. Advancement Committee, he was the lead
                        consulting physician on the development of the ARTAS robot, an image and
                        algorithm-guided system that utilizes the{" "}
                        <strong>Harris S.A.F.E. dissecting system</strong> and robotics to make
                        the best selection of follicular units for each individual.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              {tab === "bio" && (

                <div className="mx-auto max-w-6xl">
                  <div>
                    <h2 className="text-2xl md:text-3xl font-bold text-foreground">
                      Dr. James Harris' Bio
                    </h2>
                    <div className="mt-4 space-y-4 text-base leading-relaxed text-muted-foreground">
                      <p>
                        Recognized worldwide as the foremost authority in hair restoration and
                        excellent surgeon, Dr. James Harris leads our team of elite hair
                        surgeons as the medical director at RESTORE. Celebrated for
                        revolutionizing the hair restoration industry, Harris invented the{" "}
                        <strong>Harris S.A.F.E. System</strong>, a patented medical device used
                        by doctors around the world to perform the F.U.E. procedure.
                      </p>
                      <p>
                        Over his 25-year career, Dr. Harris has performed thousands of
                        successful procedures and trained hundreds of doctors worldwide on his
                        advanced hair transplantation method. As an acclaimed author and
                        international public speaker, he has been featured on TV and talk shows
                        as an expert in the evolving landscape of hair restoration.
                      </p>
                      <p className="font-semibold text-foreground">
                        Pioneering the Future of FUE (Follicular Unit Excision)
                      </p>
                      <p>
                        Dr. Harris has devoted his entire career to the advancement of hair
                        restoration and specifically F.U.E. Appointed by his peers as the
                        distinguished International Chairman of the F.U.E. Research Committee,
                        he has received numerous awards including the ISHRS' Golden Follicle
                        Award, the highest honor in the field of hair restoration, bestowed
                        upon only one doctor worldwide each year for significant clinical
                        contributions to the field of hair restoration.
                      </p>
                      <p>
                        As Chairman of the F.U.E. Advancement Committee, he was the lead
                        consulting physician on the development of the ARTAS™ robot, an image
                        and algorithm-guided system that utilizes the Harris S.A.F.E.™
                        dissecting system and robotics to make the best selection of
                        follicular units for each individual.
                      </p>
                      <p className="font-semibold text-foreground">
                        RESTORE Hair Medical Director
                      </p>
                      <p>
                        As the Medical Director of RESTORE and Board Certified Hair
                        Restoration Surgeon, Dr. Harris is supported by an exceptionally
                        experienced team of fellow hair doctors, nurses, and technicians
                        specializing in hair replacement surgery. Dr. Harris's book,{" "}
                        <em>The Hair Replacement Revolution: A Consumer's Guide to Effective
                        Hair Replacement Techniques</em>, serves as a comprehensive guide for
                        both men and women seeking solutions to their hair loss.
                      </p>
                      <p>
                        They are all committed to providing the very best medical and surgical
                        treatments for hair loss.
                      </p>
                      <p>
                        As part of this commitment, the team places patient education and the
                        highest standards of care as its top priority.
                      </p>
                      <p>
                        As time allows, Dr. Harris continues to teach and lecture around the
                        world. His focus on state-of-the-art medical and surgical techniques and
                        his fierce dedication to patient advocacy has firmly established him as a
                        prominent leader in the field of hair restoration.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {tab === "education" && (
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground">Education</h2>
                  <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2 md:items-start">
                    <div className="space-y-6">
                      <div className="border border-border p-6">
                        <p className="text-sm uppercase tracking-wide text-primary">Doctor of Medicine (M.D.) 1989</p>
                        <p className="mt-2 text-sm text-muted-foreground">University of Colorado School of Medicine</p>
                        <p className="text-sm text-muted-foreground">Denver, Colorado</p>
                      </div>
                      <div className="border border-border p-6">
                        <p className="text-sm uppercase tracking-wide text-primary">Master of Business Administration (M.B.A.) 1980</p>
                        <p className="mt-2 text-sm text-muted-foreground">Texas Tech University</p>
                        <p className="text-sm text-muted-foreground">Lubbock, Texas</p>
                      </div>
                      <div className="border border-border p-6">
                        <p className="text-sm uppercase tracking-wide text-primary">
                          Bachelor of Science (B.S.) Degree in Microbiology 1979
                        </p>
                        <p className="mt-2 text-sm text-muted-foreground">Texas Tech University</p>
                        <p className="text-sm text-muted-foreground">Lubbock Texas</p>
                      </div>
                    </div>
                    <div className="space-y-6">
                      <div className="border border-border p-6">
                        <p className="text-sm uppercase tracking-wide text-primary">Post Graduate Training</p>
                        <p className="mt-2 text-sm text-muted-foreground">
                          Residency in Otolaryngology – Head and Neck Surgery, Facial Plastic Surgery
                          University of Colorado School of Medicine
                        </p>
                        <p className="text-sm text-muted-foreground">South Denver, Colorado</p>
                        <p className="text-sm text-muted-foreground">July 1990 to June 1994</p>
                      </div>
                      <div className="border border-border p-6">
                        <p className="text-sm uppercase tracking-wide text-primary">Internship in General Surgery</p>
                        <p className="mt-2 text-sm text-muted-foreground">St. Joseph Hospital</p>
                        <p className="text-sm text-muted-foreground">Denver, Colorado</p>
                        <p className="text-sm text-muted-foreground">June 1989 to June 1990</p>
                      </div>
                    </div>
                  </div>

                </div>
              )}

              {tab === "certificates" && (
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground">Certificates</h2>
                  <ul className="mt-6 divide-y divide-border rounded-md border border-border">
                    {[
                      "Fellow of the International Society of Hair Restoration Surgery, 2013",
                      "Diplomate of the American Board of Hair Restoration Surgery, 2009",
                      "Diplomate of American Board of Otolaryngology, May 1995",
                      "Fellow American College of Surgeons, September 1995",
                      "State of Colorado Board of Medical Examiners, 1991",
                      "Diplomate of National Board of Medical Examiners, 1990",
                      "Colorado License: 30650",
                      "California License: 139377",
                      "Illinois License: 036154714",
                    ].map((c) => (
                      <li key={c} className="px-4 py-3 text-base text-muted-foreground">
                        {c}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Gallery */}
        <section className="bg-background py-12 md:py-16">
          <div className="mx-auto max-w-6xl px-4">
            <h2 className="text-center text-2xl md:text-3xl font-bold text-foreground">
              Learn More About <span className="text-primary">Dr. Harris</span>
            </h2>
            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {GALLERY.map((url, i) => (
                <img
                  key={url}
                  src={url}
                  alt={`Dr. James Harris at work ${i + 1}`}
                  loading="lazy"
                  className="w-full rounded-none object-cover"
                />
              ))}
            </div>
          </div>
        </section>

        <Suspense fallback={null}>
          <Results resultsHref="https://restorehair.com/results/" resultsButtonLabel="See More Results" />
          <Reviews />
        </Suspense>
        <SocialPosts />
        <Suspense fallback={null}>
          <QuoteForm />
        </Suspense>

      </main>
      <Footer />
    </div>
  );
}